import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { KeyRound, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function ChangePasswordPage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const goByRole = (r) => {
    if (["super_admin", "platform_manager", "platform_staff", "platform_observer", "platform_sales"].includes(r)) navigate("/admin", { replace: true });
    else if (r === "contractor") navigate("/contractor", { replace: true });
    else if (r === "customer") navigate("/customer", { replace: true });
    else navigate("/dashboard", { replace: true });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("New password must be at least 6 characters"); return; }
    if (next !== confirm) { toast.error("New password and confirmation don't match"); return; }
    if (next === current) { toast.error("New password must be different from the current one"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await refresh();
      goByRole(user?.role);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  const signOut = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6 py-10">
      <div className="w-full max-w-md">
        <div className="card-soft !p-8 sm:!p-10">
          <div className="w-12 h-12 rounded-2xl bg-[#F2CC8F] flex items-center justify-center text-[#2C2A29] mb-5">
            <KeyRound size={22} />
          </div>
          <h1 className="font-display text-3xl font-semibold text-[#2C2A29]">Set a new password</h1>
          <p className="text-[#7A7571] mt-2 mb-6">
            For your security, please change the temporary password before you continue.
          </p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Current password</label>
              <input
                data-testid="cp-current"
                type="password" required autoComplete="current-password"
                value={current} onChange={(e) => setCurrent(e.target.value)}
                className="input-soft" placeholder="Temporary password"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">New password</label>
              <input
                data-testid="cp-new"
                type="password" required minLength={6} autoComplete="new-password"
                value={next} onChange={(e) => setNext(e.target.value)}
                className="input-soft" placeholder="At least 6 characters"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-2 block">Confirm new password</label>
              <input
                data-testid="cp-confirm"
                type="password" required minLength={6} autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="input-soft" placeholder="Retype your new password"
              />
            </div>
            <button data-testid="cp-submit" disabled={busy}
              className="btn-primary w-full mt-2 inline-flex items-center justify-center gap-2">
              {busy && <Loader2 size={16} className="animate-spin" />}
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
          <button
            type="button"
            onClick={signOut}
            data-testid="cp-signout"
            className="mt-6 w-full text-sm text-[#7A7571] hover:text-[#2C2A29] inline-flex items-center justify-center gap-2"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
